// Slow Burn: a row at a time until no word fits or you put the run to bed, then its results (and,
// for a run that ended here, maybe the rating ask).
// Every move is checked locally with @wbwl/rules, shown at once and saved to the API, whose answer
// (with the next row's flame) replaces the local one; a rejection rolls it back.
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import type { RunEnd, SlowBurnInfo, SlowBurnRun } from '@wbwl/contract'
import { applyRunMove, endRun, fuseLit, runLegalWords, runWordsLeft, scoreWord, SLOW_BURN_FUSE, wordListFor } from '@wbwl/rules'
import { Keyboard } from '../components/game-ui.tsx'
import { Icon, type IconName } from '../components/Icon.tsx'
import { RatingAsk } from '../components/RatingAsk.tsx'
import { ErrorNote, Loading, NotFound, useToast } from '../components/shell.tsx'
import { RunCardPanel, RunPanel } from '../components/SlowBurnSides.tsx'
import { DepthHistogram, Fog, LiveRows, PlayedColumn, RunFuse, RunHead, RunPreview, RunWord, SlowBurnHowTo, StandingLetters } from '../components/slow-burn-ui.tsx'
import { ApiFailure, api } from '../lib/api.ts'
import { useRun, useSlowBurn, useSlowBurnBoard } from '../lib/data.ts'
import { fmt, shortDate } from '../lib/game.ts'
import { useFit } from '../lib/layout.ts'
import { copyText, nativeShare } from '../lib/share.ts'
import { lettersStanding, localRun, runRejectionText, runShareText, toRunState, toSlowBurn } from '../lib/slow-burn.ts'
import { playSubmit, playTick, primeSound } from '../lib/sound.ts'
import { invalidate, setResource } from '../lib/store.ts'

export default function SlowBurnPage() {
  const params = useParams()
  const n = params.number ? Number(params.number) : 'today'
  if (n !== 'today' && (!Number.isInteger(n) || n < 1)) return <NotFound />
  return <SlowBurnLoader n={n} />
}

function SlowBurnLoader({ n }: { n: number | 'today' }) {
  const sb = useSlowBurn(n)
  const info = sb.data
  const rq = useRun(info?.number ?? null)
  // Only a run that ends here brings the rating ask; one already over when you opened it doesn't.
  const endedOnOpen = useRef<{ n: number; ended: boolean } | null>(null)
  if (rq.data && endedOnOpen.current?.n !== rq.data.edition) endedOnOpen.current = { n: rq.data.edition, ended: !!rq.data.ended }
  if (sb.error) return <div className="page"><ErrorNote error={sb.error} retry={() => sb.reload()} /></div>
  if (!info || !rq.data) return rq.error ? <div className="page"><ErrorNote error={rq.error} retry={() => rq.reload()} /></div> : <div className="page"><Loading label="Laying the fuse…" /></div>
  return rq.data.ended ? <Results key={info.number} info={info} run={rq.data} justEnded={!endedOnOpen.current?.ended} /> : <Run key={info.number} info={info} run={rq.data} />
}

function Run({ info, run }: { info: SlowBurnInfo; run: SlowBurnRun }) {
  const toast = useToast()
  const fit = useFit()
  const edition = useMemo(() => toSlowBurn(info, run), [info, run])
  const words = useMemo(() => wordListFor(edition), [edition])
  const key = `sbrun:${info.number}`
  const [typing, setTypingState] = useState('')
  const typingRef = useRef('')
  const setTyping = (v: string) => { typingRef.current = v; setTypingState(v) }
  const busyRef = useRef(false)
  const [shake, setShake] = useState(false)
  const [flare, setFlare] = useState(false)
  const [bedArmed, setBedArmed] = useState(false)
  const [howto, setHowto] = useState(false)
  const left = useMemo(() => runWordsLeft(toRunState(run), words), [run, words])
  const invalid = typing.length === 5 && !words.has(typing)

  async function save(path: string, body: unknown, optimistic: SlowBurnRun, onFail: () => void): Promise<SlowBurnRun | null> {
    setResource(key, optimistic)
    busyRef.current = true
    try {
      const server = await api.post<SlowBurnRun>(path, body)
      setResource(key, server)
      return server
    } catch (e) {
      setResource(key, run)
      onFail()
      toast(e instanceof ApiFailure ? e.body.message || 'That didn’t go through. Try again.' : 'Couldn’t save that. Check your connection and try again.', 2600)
      return null
    } finally {
      busyRef.current = false
    }
  }

  const finished = () => invalidate('sbinfo:', 'sbboard:')

  async function submit() {
    const word = typingRef.current
    if (word.length < 5) return toast('Five letters, please')
    const r = applyRunMove(edition, toRunState(run), word, words)
    if (!r.ok) {
      toast(runRejectionText(r, word))
      setShake(true)
      setTimeout(() => setShake(false), 450)
      return
    }
    setTyping('')
    setBedArmed(false)
    setFlare(true)
    setTimeout(() => setFlare(false), 900)
    playSubmit(true)
    const lettersLeft = info.rows - r.state.ashes.length
    toast(<><span className="sc">+{r.score.total}</span><span><b>{r.burned}</b> is gone · {lettersLeft} letters left</span></>)
    if (r.state.ended) setTimeout(() => toast(<><Icon name="flame" />No word fits. The fuse is out.</>, 2400), 1900)
    // The next row's fuse starts in full at once; the server's answer brings its exact time.
    const lit = fuseLit(r.state, words)
    const optimistic = { ...localRun(info, run, r.state), fuse: lit ? { endsAt: '', msLeft: SLOW_BURN_FUSE.seconds * 1000, seconds: SLOW_BURN_FUSE.seconds } : null }
    const server = await save(`/slow-burn/${info.number}/moves`, { word }, optimistic, () => setTyping(word))
    if (server?.ended === 'fuse_out') toast(<><Icon name="clock" />The fuse ran out before that word landed</>, 2600)
    else if (server?.ended === 'day_over') toast(<><Icon name="moon" />The day is over, so your run ended with it</>, 2600)
    else if (server?.fuse && !run.fuse) setTimeout(() => toast(<><Icon name="clock" />The fuse is lit: {SLOW_BURN_FUSE.seconds} seconds a row from here</>, 2600), 1900)
    if (server?.ended) finished()
  }

  /** The fuse reached zero here: the server ends the run as fuse_out (or, if its clock disagrees, sends the run back). */
  async function fuseOut() {
    setTyping('')
    try {
      const server = await api.post<SlowBurnRun>(`/slow-burn/${info.number}/end`, { fuse: true })
      setResource(key, server)
      if (server.ended) { toast(<><Icon name="clock" />The fuse ran out. Your {fmt(server.scores.points)} points stand.</>, 2800); finished() }
    } catch {
      setResource(key, await api.get<SlowBurnRun>(`/slow-burn/${info.number}/run`).catch(() => run))
    }
  }

  async function putToBed() {
    const r = endRun(toRunState(run))
    if (!r.ok) return
    const server = await save(`/slow-burn/${info.number}/end`, {}, localRun(info, run, r.state), () => {})
    if (server) finished()
  }

  const onKey = (k: string) => {
    primeSound()
    if (k === 'ENTER') { if (!busyRef.current) void submit(); return }
    setBedArmed(false)
    const t = typingRef.current
    if (k === 'DEL') return setTyping(t.slice(0, -1))
    if (run.ashes.includes(k)) return toast(<><Icon name="flame" style={{ color: '#FF8A5C' }} />{k} burned on row {run.ashes.indexOf(k) + 1}</>)
    if (t.length < 5) { setTyping(t + k); playTick() }
  }
  const onKeyRef = useRef(onKey)
  onKeyRef.current = onKey

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      if (e.key === '?') { setHowto(true); return }
      if (e.key === 'Enter') { e.preventDefault(); onKeyRef.current('ENTER') }
      else if (e.key === 'Backspace') { e.preventDefault(); onKeyRef.current('DEL') }
      else if (/^[a-z]$/i.test(e.key)) onKeyRef.current(e.key.toUpperCase())
    }
    window.addEventListener('keydown', down)
    return () => window.removeEventListener('keydown', down)
  }, [])

  // First visit: the rules, once.
  useEffect(() => {
    try {
      if (run.rows.length === 0 && !localStorage.getItem('lof:sb-coached')) { setHowto(true); localStorage.setItem('lof:sb-coached', '1') }
    } catch { /* private mode */ }
  }, [run.rows.length])

  const kbVars = { '--kw': `${fit.keyW}px`, '--kh': `${fit.keyH}px`, '--kgap': `${fit.keyGap}px`, '--kwide': `${fit.keyWide}px` } as CSSProperties
  const lettersLeft = lettersStanding(run.ashes)
  // Narrower desktops (below SIDE_PANELS_MIN) get the one-column run.
  const wide = fit.wide
  const tile = Math.min(fit.tile, wide ? 60 : 56)

  return (
    <div className={`page sb-page${wide ? ' wide' : ''}`}>
      {wide && <RunPanel info={info} run={run} left={left} />}
      <main className="sb-main">
        {/* On desktop back sits with the run's numbers. */}
        {!fit.desktop && <div className="topbar">
          <Link className="ibtn" to="/" aria-label="Back to today"><Icon name="back" /></Link>
          <div className="title"><b>Slow Burn · No. {info.number}</b><small>{shortDate(info.date)}</small></div>
          <button type="button" className="ibtn" aria-label="How to play" onClick={() => setHowto(true)}><Icon name="help" /></button>
          <Link className="ibtn" to="/leaderboard?mode=slow-burn" aria-label="Scoreboard"><Icon name="trophy" /></Link>
        </div>}
        <RunHead
          row={run.rows.length + 1} rows={info.rows} points={run.scores.points} left={left}
          lead={fit.desktop ? <Link className="ibtn" to="/" aria-label="Back to today"><Icon name="back" /></Link> : undefined}
        />
        <div className="sb-column"><PlayedColumn info={info} run={run} show={5} size={wide ? 28 : 24} liveTile={tile} flareLast={flare} /></div>
        <LiveRows info={info} run={run} typing={typing} size={tile} invalid={invalid} shake={shake} under={<RunFuse fuse={run.fuse} size={tile} onOut={() => void fuseOut()} />} />
        <Fog row={run.rows.length} rows={info.rows} />
        <div className="sb-actions">
          {left < 100 && run.rows.length > 0 && <span className="chip burn"><Icon name="flame" />Only {lettersLeft.join(' ')} left</span>}
          {run.rows.length > 0 && (
            <button type="button" className={`btn ghost sm bed${bedArmed ? ' armed' : ''}`} onClick={() => (bedArmed ? void putToBed() : setBedArmed(true))}>
              {bedArmed ? 'Tap again to end your run' : <><Icon name="flag" size={16} />End run</>}
            </button>
          )}
        </div>
        <div className="dock" style={kbVars}>
          <div className="preview-wrap"><RunPreview info={info} word={typing} col={run.cols[run.rows.length]} invalid={invalid} /></div>
          <Keyboard edition={{ gold: info.gold, burns: [], rulesVersion: info.rulesVersion }} ashes={run.ashes} onKey={onKey} />
        </div>
      </main>
      {wide && <RunCardPanel info={info} run={run} typing={typing} valid={!invalid} />}
      <SlowBurnHowTo open={howto} onClose={() => setHowto(false)} />
    </div>
  )
}

/** How a finished run ended, as its results tag shows it. */
const ENDINGS: Record<RunEnd, { label: string; icon: IconName; tone: 'ink' | 'burn' }> = {
  burned_out: { label: 'Burned out', icon: 'flame', tone: 'burn' },
  put_to_bed: { label: 'Ended early', icon: 'flag', tone: 'ink' },
  fuse_out: { label: 'Fuse ran out', icon: 'clock', tone: 'burn' },
  day_over: { label: 'Day ended', icon: 'moon', tone: 'ink' },
}

function Results({ info, run, justEnded }: { info: SlowBurnInfo; run: SlowBurnRun; justEnded: boolean }) {
  const toast = useToast()
  const navigate = useNavigate()
  const board = useSlowBurnBoard(info.number, 'today').data
  const words = useMemo(() => wordListFor(info), [info])
  const missed = useMemo(
    () => runLegalWords(toRunState(run), words).sort((a, b) => scoreWord(b, info).total - scoreWord(a, info).total),
    [run, words, info],
  )
  const half = Math.ceil(run.rows.length / 2)
  const order = Array.from({ length: half }, (_, i) => [i, i + half]).flat().filter((r) => r < run.rows.length)

  async function share() {
    const text = runShareText(info, run)
    const r = await nativeShare(text)
    if (r === 'unavailable') toast((await copyText(text)) ? 'Copied. Paste it anywhere.' : 'Couldn’t copy that.')
  }

  return (
    <div className="page sb-results">
      <div className="topbar">
        <Link className="ibtn" to="/" aria-label="Back to today"><Icon name="close" /></Link>
        <div className="title"><b>Slow Burn · No. {info.number}</b><small>{shortDate(info.date)}{run.ranked ? '' : ' · UNRANKED'}</small></div>
        <Link className="ibtn" to="/leaderboard?mode=slow-burn" aria-label="Scoreboard"><Icon name="trophy" /></Link>
      </div>
      <div className="sb-results-body">
        <span className={`tag ${ENDINGS[run.ended!].tone}`}><Icon name={ENDINGS[run.ended!].icon} size={12} />{ENDINGS[run.ended!].label}</span>
        <h1 className="h-serif sb-deep">{fmt(run.scores.points)} <i className="burn-em">points.</i></h1>
        <div className="sb-deep-sub">{run.depth} row{run.depth === 1 ? '' : 's'} deep</div>
        <p className="muted">
          {missed.length
            ? <>{fmt(missed.length)} word{missed.length === 1 ? ' was' : 's were'} still possible, like {missed.slice(0, 3).join(', ')}.</>
            : <>No word was left to play.</>}
        </p>

        <div className="sb-stats">
          <div><span className="eyebrow">Points</span><b>{fmt(run.scores.points)}</b></div>
          <div><span className="eyebrow">Row</span><b>{run.depth}<small>/{info.rows}</small></b></div>
          <div><span className="eyebrow">Place</span><b>{run.placement ? `#${fmt(run.placement.position)}` : '—'}</b></div>
        </div>

        {board && board.players > 0 && (
          <div className="card sb-card">
            <div className="lb-sum-head"><b>Where runs ended today</b><span className="eyebrow">{fmt(board.players)} run{board.players === 1 ? '' : 's'}</span></div>
            <DepthHistogram bins={board.histogram} you={run.ranked ? run.depth : null} />
          </div>
        )}

        <StandingLetters gold={info.gold} ashes={run.ashes} rulesVersion={info.rulesVersion} />

        <div className="sb-run-head"><h2 className="h-serif">Your run</h2><span className="eyebrow">burned letter in red</span></div>
        <div className="sb-runlist">
          {order.map((r) => (
            <div key={r} className="r">
              <span className="n">{r + 1}</span>
              <RunWord gold={info.gold} rulesVersion={info.rulesVersion} word={run.rows[r]} col={run.cols[r]} size={20} gap={2} />
              <span className="s">{run.scores.rows[r].total}</span>
            </div>
          ))}
        </div>

        <div className="sb-solver">
          <Icon name={info.solver.rows ? 'crown' : 'lock'} />
          <div>
            <b>The best run found: {fmt(info.solver.points)} pts · row {info.solver.depth}</b>
            <span className="faint mono small">{info.solver.rows ? info.solver.rows.join(' ') : 'Its words print when the day is over, at midnight Pacific.'}</span>
          </div>
        </div>

        <div className="sb-buttons">
          <button type="button" className="btn ink" onClick={share}><Icon name="share" />Share</button>
          <button type="button" className="btn ghost" onClick={() => navigate('/leaderboard?mode=slow-burn')}><Icon name="trophy" />Scoreboard</button>
        </div>
      </div>
      <RatingAsk mode="slow_burn" edition={info.number} finished={justEnded} />
    </div>
  )
}

